'use client';

import { useState, useEffect, useMemo } from 'react';
import { Search, FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
// import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import type { SmokeballFile } from '@/lib/services/smokeball-migration';
import { formatFileSize } from '@/lib/utils/file-utils';
import { formatDate } from '@/lib/utils';

interface SmokeballSearchStepProps {
  matterId: number;
  onFilesSelected: (
    files: Array<{ externalId: string; fileName: string }>
  ) => void;
  error?: string;
}

export function SmokeballSearchStep({
  matterId,
  onFilesSelected,
  error
}: SmokeballSearchStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SmokeballFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<
    Map<string, SmokeballFile>
  >(() => new Map());
  const [isSearching, setIsSearching] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredResults = useMemo(() => {
    if (!searchQuery) return searchResults;
    return searchResults.filter((file: SmokeballFile) => {
      return file.fileName.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [searchResults, searchQuery]);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(
          `/api/proxy/matters/${matterId}/ai-analysis/pms-migration/document-search`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            },
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          throw new Error(
            `There was a problem querying documents for this matter`
          );
        }

        const data = await response.json();

        setIsSearching(false);

        if (!data || !data.files) {
          setLocalError('Invalid response from API');
          return;
        }

        setSearchResults(data.files || []);
      } catch (err) {
        setIsSearching(false);
        setLocalError(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    fetchResults();
  }, [matterId]);

  const toggleFileSelection = (file: SmokeballFile) => {
    // Already-in-Vale documents can't be re-imported (would create a duplicate).
    if (file.alreadyMigrated) return;

    const newSelection = new Map(selectedFiles);
    if (newSelection.has(file.externalId)) {
      newSelection.delete(file.externalId);
    } else {
      newSelection.set(file.externalId, file);
    }
    setSelectedFiles(newSelection);
  };

  // Count of already-in-Vale documents in the current (filtered) view, so the
  // user understands up front why some rows can't be selected.
  const alreadyInValeCount = useMemo(
    () => filteredResults.filter((file) => file.alreadyMigrated).length,
    [filteredResults]
  );

  // const removeSelectedFile = (externalId: string) => {
  //   const newSelection = new Map(selectedFiles);
  //   newSelection.delete(externalId);
  //   setSelectedFiles(newSelection);
  // };

  // const clearAllSelections = () => {
  //   setSelectedFiles(new Map());
  // };

  const handleContinue = () => {
    const selections = Array.from(selectedFiles.values()).map((file) => ({
      externalId: file.externalId,
      fileName: file.fileName
    }));

    onFilesSelected(selections);
  };

  const noFilterResults =
    searchResults.length !== 0 && filteredResults.length === 0;

  return (
    <div className="space-y-4">
      <div>
        {/* Search Bar */}
        {searchResults.length > 0 && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Filter documents by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        )}
      </div>

      <div className="mt-6 min-h-48">
        {/* Already-in-Vale explainer — communicates up front why some rows are
            greyed out and can't be selected, so the user avoids duplicate imports. */}
        {!isSearching && alreadyInValeCount > 0 && (
          <div className="text-muted-foreground mb-3 flex items-center gap-2 px-1 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span>
              {alreadyInValeCount} document
              {alreadyInValeCount !== 1 ? 's are' : ' is'} already in Vale and
              cannot be re-imported (marked{' '}
              <span className="font-medium">Already in Vale</span>).
            </span>
          </div>
        )}

        {/* Results Table */}
        {filteredResults.length > 0 && (
          <div className="rounded-lg">
            {/* table-fixed + colgroup gives the size/date columns a real,
                enforced width (the previous `width={}` prop on TableCell
                lands on the legacy HTML attribute, which table-layout: auto
                only treats as a hint) — otherwise an unbreakable long PMS
                filename forces the table wider than the dialog and the
                size/date columns get squeezed into wrapping instead
                (VALE-406). */}
            <Table className="table-fixed">
              <colgroup>
                <col className="w-10" />
                <col />
                <col className="w-24" />
                <col className="w-36" />
              </colgroup>
              <TableBody>
                {filteredResults.map((file) => {
                  const isSelected = selectedFiles.has(file.externalId);
                  const isAlreadyInVale = file.alreadyMigrated;
                  return (
                    <TableRow
                      key={file.externalId}
                      className={cn(
                        isAlreadyInVale
                          ? 'bg-muted/30 cursor-not-allowed'
                          : isSelected
                            ? 'bg-surface-subtle hover:bg-info-subtle cursor-pointer'
                            : 'hover:bg-muted/50 cursor-pointer'
                      )}
                      onClick={() => toggleFileSelection(file)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          disabled={isAlreadyInVale}
                          onCheckedChange={() => toggleFileSelection(file)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={
                            isAlreadyInVale
                              ? `${file.fileName} is already in Vale and cannot be re-imported`
                              : `Select ${file.fileName}`
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <FileText
                            className={cn(
                              'mt-0.5 h-4 w-4 shrink-0',
                              isAlreadyInVale
                                ? 'text-muted-foreground/60'
                                : 'text-muted-foreground'
                            )}
                          />
                          {/* The badge sits INSIDE the file-name span so it flows
                              as inline content after the name's last character.
                              As a flex sibling it was pushed to the cell's right
                              edge whenever the name wrapped, detaching it from
                              the name it annotates (VALE-89). The name column
                              still wraps rather than truncating — the fixed
                              checkbox/size/date column widths (colgroup, below)
                              are what stop a long name overflowing the dialog,
                              so the full name stays readable at any length
                              (VALE-406). */}
                          <span
                            className={cn(
                              'min-w-0',
                              isAlreadyInVale
                                ? 'text-muted-foreground font-medium'
                                : isSelected
                                  ? 'font-semibold'
                                  : 'font-medium'
                            )}
                          >
                            {file.fileName}
                            {isAlreadyInVale && (
                              <StatusBadge
                                tone="neutral"
                                icon={CheckCircle2}
                                className="ml-2 align-middle whitespace-nowrap"
                              >
                                Already in Vale
                              </StatusBadge>
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-center text-sm whitespace-nowrap',
                          isAlreadyInVale && 'text-muted-foreground'
                        )}
                      >
                        {file.sizeBytes
                          ? formatFileSize(file.sizeBytes)
                          : 'n/a'}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-right text-sm whitespace-nowrap',
                          isAlreadyInVale && 'text-muted-foreground'
                        )}
                      >
                        {formatDate(file.dateCreated)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Results Summary */}
        {!isSearching && searchQuery && filteredResults.length > 0 && (
          <div className="text-muted-foreground mt-2 flex items-center justify-between px-4 text-xs">
            <span>
              {filteredResults.length} document
              {filteredResults.length !== 1 ? 's' : ''} found
            </span>
          </div>
        )}

        {/* Loading */}
        {isSearching && (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <FileText className="text-foreground-subtle mx-auto mb-3 h-12 w-12" />
            <p className="text-muted-foreground text-sm">
              Loading documents...
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isSearching &&
          (searchResults.length === 0 || filteredResults.length === 0) && (
            <div className="rounded-lg border border-dashed p-12 text-center">
              <FileText className="text-foreground-subtle mx-auto mb-3 h-12 w-12" />
              <p className="text-muted-foreground text-sm">
                {noFilterResults
                  ? 'Your current query returned no results'
                  : 'No documents found in this Smokeball matter'}
              </p>
            </div>
          )}

        {/* Selected Files Section */}
        {/* Relevant for search query filtering only -- hidden whilst we query all */}
        {/* {selectedFiles.size > 0 && (
          <div className="mt-10 rounded-lg border bg-blue-50/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold">
                Selected Documents ({selectedFiles.size})
              </h4>
              <Button pad="lg" density="micro"
                variant="ghost"
                size="xs"
                onClick={clearAllSelections}
              >
                Clear All
              </Button>
            </div>
            <div className="space-y-2">
              {Array.from(selectedFiles.values()).map((file) => (
                <div
                  key={file.externalId}
                  className="bg-background flex items-center justify-between rounded-md border p-3"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <FileText className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {file.fileName}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {formatFileSize(file.sizeBytes)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeSelectedFile(file.externalId)}
                    className="flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove {file.fileName}</span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* Error */}
        {(error || localError) && (
          <div className="border-destructive/50 bg-destructive/10 mt-6 rounded-lg border p-3">
            <p className="text-destructive text-sm">{error || localError}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <Button
          size="xs"
          density="compact"
          onClick={handleContinue}
          disabled={selectedFiles.size === 0}
        >
          Import {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}
